"use client";

import { c as _c } from "react/compiler-runtime";
import { useDesktopAnimations } from "@/lib/breakpoints";
import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
const ease = [0.17, 0.85, 0.438, 0.99] as const;
const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='320'%3E%3Crect width='400' height='320' fill='none'/%3E%3C/svg%3E";
export type ShowcaseStep = {
  step: string;
  title: string;
  description: string;
  image?: string;
  alt?: string;
  panel?: string;
};
export type StepsShowcaseProps = {
  heading: string;
  subtitle?: string;
  steps: ShowcaseStep[];
  images?: string[];
  imageAlts?: string[];
  fallbackImage?: string;
  columns?: 2 | 3 | 4;
  stagger?: boolean;
  imageAspect?: string;
  imageFit?: "cover" | "contain";
  className?: string;
};
const COLUMN_CLASS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-2 lg:grid-cols-3",
  4: "md:grid-cols-2 lg:grid-cols-4"
};
export default function StepsShowcase(t0) {
  const $ = _c(44);
  const {
    heading,
    subtitle,
    steps,
    images,
    imageAlts,
    fallbackImage: t1,
    columns: t2,
    stagger: t3,
    imageFit: t4,
    className: t5
  } = t0;
  const fallbackImage = t1 === undefined ? PLACEHOLDER : t1;
  const columns = t2 === undefined ? 3 : t2;
  const stagger = t3 === undefined ? true : t3;
  const imageFit = t4 === undefined ? "cover" : t4;
  const className = t5 === undefined ? "" : t5;
  const desktopAnimations = useDesktopAnimations();
  const t6 = `bg-[linear-gradient(120deg,#eefbf8_0%,#f6f8fd_45%,#f2eefc_100%)] px-6 py-16 font-sans md:py-24 dark:bg-gray-950 dark:bg-none ${className}`;
  let t7;
  if ($[0] !== desktopAnimations) {
    t7 = desktopAnimations ? {
      opacity: 0,
      y: 40
    } : false;
    $[0] = desktopAnimations;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  let t8;
  if ($[2] !== desktopAnimations) {
    t8 = desktopAnimations ? {
      opacity: 1,
      y: 0
    } : undefined;
    $[2] = desktopAnimations;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  let t9;
  if ($[4] !== desktopAnimations) {
    t9 = desktopAnimations ? undefined : {
      opacity: 1,
      y: 0
    };
    $[4] = desktopAnimations;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  let t10;
  let t11;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      duration: 0.6,
      ease
    };
    t11 = {
      once: true
    };
    $[6] = t10;
    $[7] = t11;
  } else {
    t10 = $[6];
    t11 = $[7];
  }
  let t12;
  if ($[8] !== heading || $[9] !== t7 || $[10] !== t8 || $[11] !== t9) {
    t12 = <motion.h2 initial={t7} whileInView={t8} animate={t9} transition={t10} viewport={t11} className="mb-5 font-poppins text-[1.9rem] font-bold leading-tight text-[#0b1354] sm:text-[2.25rem] lg:text-[2.6rem] dark:text-white">{heading}</motion.h2>;
    $[8] = heading;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== subtitle) {
    t13 = subtitle && <p className="text-base leading-[1.75] text-[#5b6b7d] dark:text-gray-400">{subtitle}</p>;
    $[13] = subtitle;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== t12 || $[16] !== t13) {
    t14 = <div className="mx-auto mb-14 max-w-3xl text-center">{t12}{t13}</div>;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = `grid grid-cols-1 gap-7 lg:gap-8 ${COLUMN_CLASS[columns]}`;
  let t16;
  if ($[18] !== columns || $[19] !== desktopAnimations || $[20] !== fallbackImage || $[21] !== imageAlts || $[22] !== imageFit || $[23] !== images || $[24] !== stagger || $[25] !== steps) {
    let t17;
    if ($[27] !== columns || $[28] !== desktopAnimations || $[29] !== fallbackImage || $[30] !== imageAlts || $[31] !== imageFit || $[32] !== images || $[33] !== stagger) {
      t17 = (step, idx) => {
        const offset = stagger && idx % columns === 1 ? "lg:-translate-y-8" : "";
        const src = images?.[idx] ?? step.image ?? fallbackImage;
        const alt = imageAlts?.[idx] ?? step.alt ?? step.title;
        const panel = step.panel === "pink" ? "bg-gradient-to-b from-[#fce0e6] to-[#d5a8e8] dark:from-purple-950 dark:to-indigo-950" : "bg-gradient-to-b from-[#d9b0ea] to-[#fcdfe6] dark:from-indigo-950 dark:to-purple-950";
        return <div key={`${step.step}-${step.title}`} className={offset}><motion.div initial={desktopAnimations ? {
            opacity: 0,
            y: 30
          } : false} whileInView={desktopAnimations ? {
            opacity: 1,
            y: 0
          } : undefined} animate={desktopAnimations ? undefined : {
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: idx % columns * 0.1,
            ease
          }} viewport={{
            once: true
          }} className="group overflow-hidden rounded-[14px] bg-white shadow-[0_10px_40px_-18px_rgba(13,38,76,0.25)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_18px_50px_-18px_rgba(13,38,76,0.35)] h-full flex flex-col dark:bg-gray-800 dark:shadow-[0_10px_40px_-18px_rgba(0,0,0,0.5)]"><div className={`relative w-full overflow-hidden aspect-[4/3] ${panel}`}><Image src={src} alt={alt} fill={true} sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" loading="lazy" className={imageFit === "contain" ? "object-contain" : "object-cover"} onError={_temp} /></div><div className="px-7 pb-9 pt-6 flex-1"><p className="mb-2 font-poppins text-[2rem] font-bold italic leading-none text-[#1877e6] dark:text-blue-400">{step.step}</p><h3 className="mb-2.5 font-poppins text-[1.35rem] font-bold leading-snug text-[#0b1354] dark:text-white">{step.title}</h3><p className="text-[15px] leading-[1.7] text-[#5b6b7d] dark:text-gray-300">{step.description}</p></div></motion.div></div>;
      };
      $[27] = columns;
      $[28] = desktopAnimations;
      $[29] = fallbackImage;
      $[30] = imageAlts;
      $[31] = imageFit;
      $[32] = images;
      $[33] = stagger;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    t16 = steps.map(t17);
    $[18] = columns;
    $[19] = desktopAnimations;
    $[20] = fallbackImage;
    $[21] = imageAlts;
    $[22] = imageFit;
    $[23] = images;
    $[24] = stagger;
    $[25] = steps;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <div className={t15}>{t16}</div>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t14 || $[39] !== t17) {
    t18 = <div className="mx-auto w-full max-w-7xl">{t14}{t17}</div>;
    $[38] = t14;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18 || $[42] !== t6) {
    t19 = <section className={t6}>{t18}</section>;
    $[41] = t18;
    $[42] = t6;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  return t19;
}
function _temp(e) {
  e.currentTarget.onerror = null;
  e.currentTarget.src = PLACEHOLDER;
}